/**
 * The datasets the admin-only Usage Reports view can render. Each key maps 1:1
 * to a Redshift reporting materialized view (wired in the report backend's
 * dataset registry). Adding a new MV to the report = one entry here + one entry
 * in the backend registry — nothing else.
 *
 * `datasetKeys` is the source-of-truth literal list (decoders/types derive from it);
 * `datasetLabels` carries the human labels, and `datasets` is the ordered pairing the
 * frontend dropdown consumes.
 *
 * The label map is a `Record` keyed on the literal union, NOT a second array. A key
 * added to `datasetKeys` without a label used to compile fine and silently never
 * appear in the dropdown — the same silent-omission mode `registry.spec.ts` guards
 * against on the backend side. Now it fails the build.
 */
export const datasetKeys = [
	'activeOrganizations',
	'transactionsByCountry',
	'difficultyRatings',
	'designTime',
	'reviewFormEntries',
	'jobsLoadedBySource',
	'documentActivity',
	'ifcUploads',
	'emailActivity',
	'chatActivity',
	'frameFiles',
	'planMatched',
	'activeUsers',
	'fpcExecuted',
	'activityLog',
	'pageUsage',
	'profileViews',
] as const;

export type DatasetKey = (typeof datasetKeys)[number];

/** Display label per dataset. Exhaustive by type — a missing key fails the build. */
export const datasetLabels: Record<DatasetKey, string> = {
	activeOrganizations: 'Active Organizations',
	transactionsByCountry: 'Transactions by Country',
	difficultyRatings: 'Job Difficulty Ratings',
	designTime: 'Design Time',
	reviewFormEntries: 'Review Form Entries',
	jobsLoadedBySource: 'Jobs Loaded by Source',
	documentActivity: 'Document Uploads & Downloads',
	ifcUploads: 'IFC Uploads (Whole House)',
	emailActivity: 'Outbound Email',
	chatActivity: 'Chat Messages',
	frameFiles: 'AI Frame Files',
	planMatched: 'Plan Matching',
	activeUsers: 'Active Users',
	fpcExecuted: 'Floor Plan Comparisons',
	activityLog: 'Activity Log',
	pageUsage: 'Web Page Visits',
	profileViews: 'Profile Views',
};

/**
 * The dropdown's options, in `datasetKeys` order.
 *
 * Derived rather than hand-maintained: the order comes from the key list and the
 * labels from the exhaustive map, so neither can drift from the other.
 */
export const datasets: { key: DatasetKey; label: string }[] = datasetKeys.map((key) => ({
	key,
	label: datasetLabels[key],
}));
